import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import { Box, Divider } from "@mui/material";
import React, { useEffect } from "react";
import { Label } from "@/components/Label";
import { ButtonVariantOption, SubmitButton } from "@/components/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  type CompanyInfoFormData,
  CompanyInfoFormDataSchema,
} from "@/features/accountSettings/types/schemas";
import { CTextField } from "@/components/TextField/CTextField";
import {
  CompanyInfoGetAPI,
  CompanyInfoUpdateAPI,
} from "@/features/accountSettings/api/PropertySummaryCompanyInfoAPI";

export const PropertySummaryCompanyInfo: React.FC = (_props) => {
  const companyInfo = CompanyInfoGetAPI();
  const [companyOverviewMutate] = CompanyInfoUpdateAPI();

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<CompanyInfoFormData>({
    mode: "all",
    defaultValues: {
      companyOverview: "",
    },
    resolver: zodResolver(CompanyInfoFormDataSchema),
  });

  useEffect(() => {
    // テキストボックスに何か入力してしばらく放置すると、再びサーバーからデータを取りに行った時に入力中の値が上書きされてしまう
    // そこで、一度でも入力があったら更新しないようにする
    if (reactHookForm.formState.isDirty) {
      return;
    }

    if (companyInfo) {
      reactHookForm.setValue("companyOverview", companyInfo.companyOverview);
    }
  }, [companyInfo]);

  const onSubmit = reactHookForm.handleSubmit(
    async (data: CompanyInfoFormData) => {
      const result = await companyOverviewMutate.trigger({
        companyOverview: data.companyOverview,
      });
      if (result) {
        toast.success("設定を変更しました");

        // BEへの設定値で上書きと、dirtyをリセット
        reactHookForm.reset({ companyOverview: result.companyOverview });
      }
    }
  );

  const overviewText = reactHookForm.watch("companyOverview") ?? "";

  return (
    <SectionPaper>
      <Box sx={{ display: "flex" }}>
        <SectionTitle>物件概要書用 会社情報</SectionTitle>
      </Box>
      <Divider />
      <Box>
        <Box sx={{ m: 2 }}>
          <Label>物件概要書用テキスト</Label>
          <Box sx={{ mb: 2 }}>
            <CTextField
              multiline
              rows={3}
              name={"companyOverview"}
              control={reactHookForm.control}
              size={SizingWrapperStyle.INHERIT}
              label={"自由入力"}
            />
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                justifyContent: "flex-end",
                color: "text.secondary",
                fontSize: 12,
              }}
            >
              {overviewText.length}/500
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          mr: 2,
          my: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <SubmitButton
          onClick={(_event, cancelLoading) => {
            onSubmit()
              .then(() => {
                cancelLoading();
              })
              .catch((reason) => {
                cancelLoading();
                console.error(reason);
              });
          }}
          timeout={30 * 1000}
          label={"保存"}
          disabled={!reactHookForm.formState.isDirty}
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
          loadingIndicator={"設定中..."}
        />
      </Box>
    </SectionPaper>
  );
};
